import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Mail, Phone, Download, Eye, Trash2, Megaphone, MessageSquare, Users, Check, Calendar, X } from 'lucide-react';

const LeadsCenterManagement = () => {
  const [requestInfoLeads, setRequestInfoLeads] = useState([]);
  const [marketingLeads, setMarketingLeads] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedLead, setSelectedLead] = useState(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      const [infoRes, marketingRes, messagesRes] = await Promise.all([
        axios.get(`${API}/admin/request-info`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/marketing-leads`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/contact-messages`, { headers }).catch(() => ({ data: [] })),
      ]);
      setRequestInfoLeads(infoRes.data.map(l => ({ ...l, _type: 'request-info' })));
      setMarketingLeads(marketingRes.data.map(l => ({ ...l, _type: 'marketing' })));
      setContactMessages(messagesRes.data.map(l => ({
        ...l,
        _type: 'message',
        full_name: l.name,
        phone: l.phone || '',
      })));
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const allLeads = useMemo(() => {
    let combined = [];
    if (activeFilter === 'all' || activeFilter === 'request-info') combined.push(...requestInfoLeads);
    if (activeFilter === 'all' || activeFilter === 'marketing') combined.push(...marketingLeads);
    if (activeFilter === 'all' || activeFilter === 'message') combined.push(...contactMessages);
    combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return combined;
  }, [requestInfoLeads, marketingLeads, contactMessages, activeFilter]);

  const counts = useMemo(() => ({
    all: requestInfoLeads.length + marketingLeads.length + contactMessages.length,
    'request-info': requestInfoLeads.length,
    marketing: marketingLeads.length,
    message: contactMessages.length,
  }), [requestInfoLeads, marketingLeads, contactMessages]);

  const newCount = useMemo(() => {
    const unreadInfo = requestInfoLeads.filter(l => !l.is_read).length;
    const unreadMsg = contactMessages.filter(l => !l.is_read).length;
    return unreadInfo + unreadMsg;
  }, [requestInfoLeads, contactMessages]);

  const deleteLead = async (lead, e) => {
    e?.stopPropagation();
    if (!window.confirm('Delete this lead?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      if (lead._type === 'request-info') {
        await axios.delete(`${API}/admin/request-info/${lead.id}`, { headers });
      } else if (lead._type === 'message') {
        await axios.delete(`${API}/admin/contact-messages/${lead.id}`, { headers });
      }
      toast.success('Deleted');
      if (selectedLead?.id === lead.id) setSelectedLead(null);
      fetchAll();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const markAsRead = async (lead) => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      if (lead._type === 'message') {
        await axios.patch(`${API}/admin/contact-messages/${lead.id}/read`, {}, { headers });
      }
      fetchAll();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const exportAll = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      let csvData = '';
      
      if (activeFilter === 'all' || activeFilter === 'request-info') {
        const res = await axios.get(`${API}/admin/request-info/export`, { headers });
        csvData += res.data.csv || '';
      }
      if (activeFilter === 'all' || activeFilter === 'marketing') {
        const res = await axios.get(`${API}/admin/marketing-leads/export`, { headers });
        if (csvData) csvData += '\n';
        csvData += res.data.csv || '';
      }
      
      if (!csvData) { toast.error('No data to export'); return; }
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${activeFilter}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Exported successfully');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case 'request-info':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-500/20 text-blue-400">Request Info</span>;
      case 'marketing':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-500/20 text-green-400">Marketing</span>;
      case 'message':
        return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-500/20 text-purple-400">Message</span>;
      default:
        return null;
    }
  };

  const filters = [
    { id: 'all', label: 'All Leads', icon: Users },
    { id: 'request-info', label: 'Request Info', icon: Eye },
    { id: 'message', label: 'Messages', icon: MessageSquare },
    { id: 'marketing', label: 'Marketing Opt-Ins', icon: Megaphone },
  ];

  return (
    <div data-testid="leads-center">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Leads Center</h1>
          <p className="text-[#a8b8d0] text-sm mt-1">
            {counts.all} total lead(s){newCount > 0 && <span className="text-[#ff6b35] ml-2">{newCount} new</span>}
          </p>
        </div>
        <button onClick={exportAll} className="btn-primary inline-flex items-center gap-2" data-testid="leads-export-btn">
          <Download size={18} /> Export CSV
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-4">
        {filters.map(f => {
          const Icon = f.icon;
          return (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeFilter === f.id
                  ? 'bg-[#ff6b35] text-white'
                  : 'bg-[#132238] text-[#a8b8d0] hover:bg-[#1a2d47] border border-[#1a2d47]'
              }`}
              data-testid={`leads-filter-${f.id}`}
            >
              <Icon size={14} />
              {f.label}
              <span className="text-xs opacity-70 ml-1">({counts[f.id]})</span>
            </button>
          );
        })}
      </div>

      {/* Lead Detail Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedLead(null)}>
          <div className="bg-[#132238] rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white">Lead Details</h2>
                {getTypeBadge(selectedLead._type)}
              </div>
              <button onClick={() => setSelectedLead(null)} className="text-[#a8b8d0] hover:text-white">
                <X size={20} />
              </button>
            </div>
            
            <div className="space-y-4">
              {selectedLead._type === 'marketing' && (
                <div className="bg-[#22c55e]/20 border border-[#22c55e] rounded-lg p-3">
                  <p className="text-[#22c55e] text-sm font-medium flex items-center gap-2">
                    <Check size={16} /> Opted in for Marketing Communications
                  </p>
                </div>
              )}

              {selectedLead.vehicle_name && (
                <div className="bg-[#1a2d47]/50 rounded-lg p-3">
                  <p className="text-[#a8b8d0] text-xs">Interested In</p>
                  <p className="text-white font-medium">{selectedLead.vehicle_name}</p>
                </div>
              )}
              
              <div className="space-y-2">
                <p className="text-white font-medium text-lg">{selectedLead.full_name}</p>
                {selectedLead.email && (
                  <p className="flex items-center gap-2 text-[#a8b8d0]">
                    <Mail size={16} /> {selectedLead.email}
                  </p>
                )}
                {selectedLead.phone && (
                  <p className="flex items-center gap-2 text-[#a8b8d0]">
                    <Phone size={16} /> {selectedLead.phone}
                  </p>
                )}
              </div>
              
              {selectedLead.message && (
                <div className="bg-[#1a2d47]/50 rounded-lg p-3">
                  <p className="text-[#a8b8d0] text-xs mb-1">Message</p>
                  <p className="text-white">{selectedLead.message}</p>
                </div>
              )}
              
              <div className="flex justify-between items-center text-sm text-[#a8b8d0]">
                <span>Source: {selectedLead.source || 'Contact Form'}</span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(selectedLead.created_at).toLocaleString()}
                </span>
              </div>
              
              <div className="flex gap-3 pt-4 border-t border-[#1a2d47]">
                {selectedLead.email && (
                  <a href={`mailto:${selectedLead.email}`} className="btn-primary flex-1 text-center">
                    Send Email
                  </a>
                )}
                {selectedLead.phone && (
                  <a href={`tel:${selectedLead.phone}`} className="btn-accent flex-1 text-center">
                    Call
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Leads Table */}
      <div className="bg-[#132238] rounded-xl border border-[#1a2d47] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#1a2d47]">
              <tr>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Contact</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Type</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Vehicle / Subject</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Message</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Date</th>
                <th className="text-left text-[#a8b8d0] px-4 py-3 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-[#a8b8d0]">Loading...</td>
                </tr>
              ) : allLeads.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-8 text-center text-[#a8b8d0]">
                    <Users size={48} className="mx-auto mb-4 opacity-50" />
                    <p>No leads found</p>
                  </td>
                </tr>
              ) : (
                allLeads.map((lead, idx) => (
                  <tr
                    key={`${lead._type}-${lead.id || idx}`}
                    className={`border-t border-[#1a2d47] hover:bg-[#1a2d47]/50 cursor-pointer ${
                      !lead.is_read ? 'bg-[#ff6b35]/5' : ''
                    }`}
                    onClick={() => {
                      setSelectedLead(lead);
                      if (!lead.is_read && lead._type === 'message') markAsRead(lead);
                    }}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {!lead.is_read && <span className="w-2 h-2 rounded-full bg-[#ff6b35] shrink-0"></span>}
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{lead.full_name}</p>
                          <p className="text-[#a8b8d0] text-sm truncate">{lead.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">{getTypeBadge(lead._type)}</td>
                    <td className="px-4 py-3 text-[#a8b8d0] text-sm">
                      {lead.vehicle_name || 'General Inquiry'}
                    </td>
                    <td className="px-4 py-3 text-[#a8b8d0] text-sm max-w-[200px] truncate">
                      {lead.message || '—'}
                    </td>
                    <td className="px-4 py-3 text-[#a8b8d0] text-sm whitespace-nowrap">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button className="text-[#ff6b35] hover:text-[#ffc107] p-1" title="View">
                          <Eye size={16} />
                        </button>
                        {lead._type !== 'marketing' && (
                          <button
                            onClick={(e) => deleteLead(lead, e)}
                            className="text-red-400 hover:text-red-300 p-1"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LeadsCenterManagement;
